/**
 * effectivePricing.js — the price a customer is actually billed.
 *
 * GENERATED COPY of lambda/core/effective-pricing.js (ESM for the admin console).
 * Change that file, then mirror it here. It mirrors determineEffectiveCustomerPricing in
 * lambda/lambda-recurring-billing.js, which is what actually charges the card.
 * Anything that quotes, reports on or extends a customer's price must agree with
 * that function, so use this rather than reaching into pricing.* by hand.
 *
 * Parallel copies, kept behaviourally identical and guarded by
 * scripts/test-portal-price-parity.mjs (it checks all of them against the billing
 * lambda across every live customer record):
 *   • marketplace/lib/pricing.js                        (customer portal, ESM)
 *   • admin-portal/src/admin/utils/effectivePricing.js  (admin console, ESM)
 * There is no shared-package mechanism spanning lambda/, marketplace/ and
 * admin-portal/, so the duplication is deliberate — the test is what keeps it honest.
 *
 * Two traps this exists to stop repeating:
 *   1. Discounts have been written to the PLURAL pricing.overrides array since
 *      mid-2026. Readers that only looked at the SINGULAR pricing.override showed
 *      11 customers $89.99 while they were charged $79.99.
 *   2. override.status goes stale — an expired override keeps status 'active'
 *      indefinitely. Decide on the DATES, never on status.
 */

/**
 * Pick the override the billing run would pick.
 *
 * Reproduces a deliberate quirk: the lambda's .find() matches on endDate ONLY and
 * checks startDate afterwards, so the first not-yet-expired entry wins even when it
 * has not started — and the run then falls through to baseAmount without examining
 * later entries. A "smarter" search here would report a price the run does not
 * charge. Store overrides ordered by ascending endDate.
 */
const selectOverride = (pricing, now) => {
  if (pricing.override) return pricing.override;
  if (Array.isArray(pricing.overrides) && pricing.overrides.length > 0) {
    return pricing.overrides.find(
      (o) => !o.endDate || now <= new Date(o.endDate).getTime()
    );
  }
  return undefined;
};

const asEpoch = (at) => {
  if (at instanceof Date) return at.getTime();
  if (typeof at === 'number' && Number.isFinite(at)) return at;
  return Date.now();
};

/**
 * @param {object|null|undefined} customer  customer record, legacy fields flattened
 * @param {number|null} planBasePrice       catalogue price for customer.plan, if known
 * @param {Date|number} [at]                clock to evaluate against
 * @returns {{effectivePrice:number|null, baseAmount:number|null,
 *            discountPerMonth:number, override:object|null, endDate:string|null}}
 */
export const resolveEffectivePricing = (customer, planBasePrice = null, at = undefined) => {
  const empty = {
    effectivePrice: null, baseAmount: null, discountPerMonth: 0,
    override: null, endDate: null,
  };
  if (!customer) return empty;

  // Accept either a flattened record or one that still nests legacy.*
  const pricing = customer.pricing || customer.legacy?.pricing || {};
  const now = asEpoch(at);

  let baseAmount = null;
  if (typeof pricing.baseAmount === 'number') baseAmount = pricing.baseAmount;
  else if (typeof planBasePrice === 'number') baseAmount = planBasePrice;
  else if (typeof customer.basePrice === 'number') baseAmount = customer.basePrice;
  else if (typeof customer.monthlyFee === 'number') baseAmount = customer.monthlyFee;

  const override = selectOverride(pricing, now);
  let effectivePrice = baseAmount;
  let appliedOverride = null;

  if (override && typeof override.amount === 'number') {
    const startOk = !override.startDate || now >= new Date(override.startDate).getTime();
    const notExpired = !override.endDate || now <= new Date(override.endDate).getTime();

    if (startOk && notExpired) {
      effectivePrice = override.amount;
      appliedOverride = override;
    } else if (override.endDate && now > new Date(override.endDate).getTime()) {
      if (typeof override.revertTo === 'number') {
        baseAmount = override.revertTo;
      } else if (override.revertTo === 'plan' && typeof planBasePrice === 'number') {
        baseAmount = planBasePrice;
      }
      effectivePrice = baseAmount;
    } else {
      effectivePrice = baseAmount;
    }
  }

  const discountPerMonth =
    appliedOverride && typeof baseAmount === 'number'
      ? Math.max(0, Math.round((baseAmount - appliedOverride.amount) * 100) / 100)
      : 0;

  return {
    effectivePrice,
    baseAmount,
    discountPerMonth,
    override: appliedOverride,
    endDate: appliedOverride?.endDate || null,
  };
};

/**
 * The override currently in force, from either shape — or null.
 * For callers that want the block itself (endDate, promoCode, reason) rather than
 * a price, e.g. surfacing a promo expiry or extending an existing offer.
 */
export const resolveActiveOverride = (customer, at = undefined) =>
  resolveEffectivePricing(customer, null, at).override;
